import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

// Local imports
import ListingPageTemplate from "@Pages/ListingPageTemplate/ListingPageTemplate";
import themeCreator from "@Themes/themeCreator";
import axiosInstance from "@API/axios";
import { setSnackbar } from "../../../Redux/snackBarSlice";
import usePaginate from "@Hooks/usePaginate";

// ===================== CONSTANTS - URLs & STRINGS =====================
// Default values
const DEFAULT_RESPONSE_FORMAT = "0-0";
const DEFAULT_RESPONSE_DISPLAY = "0/0";
const INITIAL_PAGE_SIZE = 10;

// UI text constants
const ERROR_FETCHING_DATA = "Error fetching data";
const EXPORT_FILENAME = "myResponses.csv";

// Special characters
const UNIT_SEPARATOR = "\u001F";
const RESPONSE_SEPARATOR = "-";
const RESPONSE_DISPLAY_SEPARATOR = "/";

// Filter operators

// ===================== HELPER FUNCTIONS =====================
/**
 * Formats response string from "X-Y" format to "X/Y" format
 * @param {object} params - DataGrid row params
 * @returns {string} Formatted response string
 */
const formatResponseValue = (params) => {
  const response = params.row.response || DEFAULT_RESPONSE_FORMAT;
  return response.replace(RESPONSE_SEPARATOR, RESPONSE_DISPLAY_SEPARATOR);
};

/**
 * Transforms data with list labels
 * @param {array} data - Raw data array
 * @param {object} listLabel - List label configuration
 * @returns {array} Transformed data
 */
const transformDataWithListLabel = (data, listLabel) => {
  if (!listLabel || !data) return data;

  return data.map((item) => ({
    ...item,
    [listLabel.listName]: (item[listLabel.listName] || [])
      .map((i) => i[listLabel.listAttr]?.trim().toUpperCase())
      .join(UNIT_SEPARATOR),
  }));
};


// ===================== MAIN COMPONENT =====================
function GenericRnDListing({
  pageTitle,
  columns,
  renderActions,
  title,
  PageIcon,
  iconColor,
  buttons = [],
  apiRoute,
  defaultColumns,
  renderCustomColumn = null,
  errorMessage,
  extraComponents = null,
  listLabel = null,
  initialColumnVisibilityModel = {},
  serverPagination = true,
  rows: externalRows,
  setRows: externalSetRows,
  getRowId,
  exportApi = null,
  exportRowToExitRequestId = null,
}) {
  // ===== Component State =====
  const [internalRows, internalSetRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);

  // ===== Hooks =====
  const navigate = useNavigate();
  const theme = themeCreator();
  const dispatch = useDispatch();
  const { loading: paginateLoading, getPage } = usePaginate();

  // ===== Determine row state (external or internal) =====
  const rows = externalRows !== undefined ? externalRows : internalRows;
  const setRows =
    externalSetRows !== undefined ? externalSetRows : internalSetRows;

  // ===== Data Fetching =====
  /**
   * Fetches data from the API with pagination and filtering support
   * @param {object} queryOptions - Query options from DataGrid
   * @param {object} filterModel - Filter model from DataGrid
   */
  const fetchPage = (queryOptions, filterModel) => {
    const filterItems = (filterModel?.items || []).map((item) => {
      const cleaned = { ...item };
      if (cleaned.value && typeof cleaned.value === "string") {
        cleaned.value = cleaned.value.trim();
        if (cleaned.field === "state") {
          cleaned.value = cleaned.value.replace(/\s+/g, "_");
        }
      }
      return cleaned;
    }).filter((item) => item.field && item.operator);

    getPage(
      apiRoute,
      errorMessage || ERROR_FETCHING_DATA,
      queryOptions,
      filterItems
    ).then((data) => {
      if (data) {
        const transformedData = listLabel
          ? transformDataWithListLabel(data.content, listLabel)
          : data.content || [];

        setRows(transformedData);
        setRowCount(data.totalElements || 0);
      }
    });
  };

  /**
   * Fetches all data without pagination
   */
  const fetchAllData = async () => {
    setPageLoading(true);
    try {
      const response = await axiosInstance.get(apiRoute);
      const data = response.data || [];
      const transformedData = listLabel
        ? transformDataWithListLabel(data, listLabel)
        : data;

      setRows(transformedData);
      setRowCount(data.length);
    } catch (error) {
      handleFetchError(error);
    } finally {
      setPageLoading(false);
    }
  };

  /**
   * Handles errors during data fetching
   */
  const handleFetchError = (error) => {
    console.error(errorMessage || ERROR_FETCHING_DATA, error);
    setRows([]);
    setRowCount(0);

    if (errorMessage) {
      dispatch(
        setSnackbar({
          open: true,
          message: errorMessage || ERROR_FETCHING_DATA,
          error: true,
        })
      );
    }
  };

  // ===== Column Processing =====
  /**
   * Gets custom string operators for filtering
   */

  /**
   * Processes columns to add custom operators and renderers
   */
  const processColumns = () => {
    const columnsToUse = columns || defaultColumns || [];

    return columnsToUse.map((col) => {
      // Handle response column
      if (col.field === "response") {
        return {
          ...col,
          valueGetter: formatResponseValue,
          renderCell: renderCustomColumn || renderResponseCell,
        };
      }

      // Handle actions column
      if (col.field === "actions") {
        return {
          ...col,
          renderCell: (params) => renderActions(params, navigate, theme),
        };
      }

      return col;
    });
  };

  /**
   * Default renderer for response cell
   */
  const renderResponseCell = (params) => {
    const formattedValue = params.value || DEFAULT_RESPONSE_DISPLAY;
    const [replied, total] = formattedValue
      .split(RESPONSE_DISPLAY_SEPARATOR)
      .map(Number);
    const allRDsReplied = replied === total;

    return (
      <span
        style={
          allRDsReplied ? styles.responseComplete : styles.responseIncomplete
        }
      >
        {formattedValue}
      </span>
    );
  };

  // ===== Table Configuration =====
  const tableProps = {
    data: rows,
    columns: processColumns(),
    disableRowSelectionOnClick: true,
    disableColumnMenu: true,
    initialColumnVisibilityModel: initialColumnVisibilityModel || {},
    rowCount,
    rows: rows || [],
    isLoading: serverPagination ? paginateLoading : pageLoading,
    fetchPage,
    columnsEnable: true,
    filtersEnable: true,
    densityEnable: true,
    exportEnable: false,
    exportedFileName: EXPORT_FILENAME,
    searchBarEnable: true,
    InitialPageSize: INITIAL_PAGE_SIZE,
    // Null when the page has not opted in, in which case no export button renders.
    // rowToExitRequestId is only used by client-filtered listings (serverPagination false), where
    // the grid rather than the server decides which rows are visible.
    excelExport: exportApi
      ? {
          endpoint: exportApi,
          fileNamePrefix: "exit-requests",
          rowToExitRequestId: exportRowToExitRequestId,
        }
      : null,
    ...(getRowId && { getRowId }),
  };

  // ===== Effects =====
  useEffect(() => {
    // Fetch all data on mount if not using server pagination
    if (!serverPagination) {
      fetchAllData();
    }
  }, [serverPagination, apiRoute]);

  // ===== Render =====
  return (
    <Box sx={styles.mainContainer}>
      <ListingPageTemplate
        title={title || pageTitle}
        PageIcon={PageIcon}
        iconColor={iconColor}
        buttons={buttons}
        tableProps={tableProps}
        serverPagination={serverPagination}
      />
      {extraComponents}
    </Box>
  );
}

// ===================== STYLES =====================
const styles = {
  mainContainer: {
    p: 0,
  },
  responseComplete: {
    color: "green",
    fontStyle: "normal",
    display: "flex",
    alignItems: "center",
  },
  responseIncomplete: {
    color: "grey",
    fontStyle: "italic",
    display: "flex",
    alignItems: "center",
  },
};

export default GenericRnDListing;
